import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import SearchBar from './SearchBar';
import PrismButton from '../prism/PrismButton';
import { ArrowIcon, CloseIcon, MenuBarsIcon, PlusIcon } from '../prism/icons';

// The top bar speaks elebyte.io's language: the cursive wordmark (with a "Support" tag) on the left,
// prism pills on the right, and MENU opening a full-screen navy panel behind the 14-slat cyan shutter.
// Wide screens also get the three destinations inline. Everything collapses into the menu on phones.

const ELEBYTE = 'https://elebyte.io';
const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex="-1"])';

export const ELEBYTE_LINKS = [
  { label: 'elebyte.io', href: ELEBYTE },
  { label: 'Book a call', href: `${ELEBYTE}/book-call` },
  { label: 'Talk to an agent', href: `${ELEBYTE}/talk-to-agent` },
  { label: 'Ask Elebyte', href: `${ELEBYTE}/ask-elebyte` },
];

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, login, logout } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const panelRef = useRef(null);
  const scopeRef = useRef(null);
  const returnFocus = useRef(null);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Knowledge Base', path: '/kb' },
    { label: 'Search', path: '/kb/search', menuOnly: true },
    { label: 'My Tickets', path: '/tickets', auth: true },
    { label: 'Submit a Ticket', path: '/tickets/new', auth: true, menuOnly: true },
  ].filter((item) => !item.auth || isAuthenticated);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/tickets') return location.pathname === '/tickets' || /^\/tickets\/(?!new)/.test(location.pathname);
    if (path === '/kb') return location.pathname.startsWith('/kb') && !location.pathname.startsWith('/kb/search');
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // A route change closes the menu (its links navigate).
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // While open: the page doesn't scroll, Escape closes, focus moves in, and goes back to MENU after.
  useEffect(() => {
    if (!menuOpen) return undefined;
    returnFocus.current = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const panel = panelRef.current;
    const first = panel?.querySelector('a, button');
    first?.focus({ preventScroll: true });
    // Everything outside the header and menu (the skip link, the page, the footer) is inert while open.
    const scope = scopeRef.current;
    const outside = Array.from(document.querySelectorAll('.sp-skip, #main, .sp-footer'))
      .filter((el) => !scope?.contains(el) && !el.hasAttribute('inert'));
    outside.forEach((el) => el.setAttribute('inert', ''));
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); setMenuOpen(false); return; }
      if (e.key !== 'Tab' || !scope) return;
      // Tab cycles inside the header and menu: past the last control it comes back to the first, and back.
      const focusable = Array.from(scope.querySelectorAll(FOCUSABLE)).filter((el) => !el.closest('[inert]'));
      if (focusable.length === 0) return;
      const firstEl = focusable[0];
      const lastEl = focusable[focusable.length - 1];
      const inside = scope.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === firstEl || !inside)) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && (document.activeElement === lastEl || !inside)) { e.preventDefault(); firstEl.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKey);
      outside.forEach((el) => el.removeAttribute('inert'));
      // Back to MENU only if focus would otherwise be lost: after a menu link, the new page's title has it.
      const back = returnFocus.current;
      const active = document.activeElement;
      const lost = !active || active === document.body || panel?.contains(active);
      if (lost && back && document.contains(back)) back.focus({ preventScroll: true });
    };
  }, [menuOpen]);

  const handleLogoutClick = () => {
    setMenuOpen(false);
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    logout();
    setLogoutDialogOpen(false);
    navigate('/');
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  return (
    <>
      {/* The bar and the menu are one modal scope while the menu is open: its Close button lives in the bar. */}
      <div
        ref={scopeRef}
        role={menuOpen ? 'dialog' : undefined}
        aria-modal={menuOpen ? 'true' : undefined}
        aria-label={menuOpen ? 'Site menu' : undefined}
      >
        <header className={`sp-bar${scrolled ? ' is-scrolled' : ''}${menuOpen ? ' is-menu' : ''}`}>
          <Link to="/" className="sp-logo" aria-label="Elebyte Support home">
            <span className="sp-logo__mark">
              <img src="/cursive-logo.svg" alt="" />
              <span className="sp-logo__sheen" aria-hidden="true" />
            </span>
            <span className="sp-sr">Elebyte</span>
            <span className="sp-logo__tag">Support</span>
          </Link>

          <div className="sp-bar__nav">
            {!menuOpen && (
              <nav className="sp-bar__links" aria-label="Main">
                {navItems.filter((item) => !item.menuOnly).map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sp-navlink${isActive(item.path) ? ' is-active' : ''}`}
                    aria-current={isActive(item.path) ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            )}
            {!menuOpen && location.pathname !== '/' && !location.pathname.startsWith('/kb/search') && (
              <div className="sp-bar__search"><SearchBar variant="compact" /></div>
            )}
            {!menuOpen && (isAuthenticated ? (
              <PrismButton
                className="sp-bar__cta"
                label="Submit Ticket"
                icon={<PlusIcon />}
                to="/tickets/new"
              />
            ) : (
              <PrismButton className="sp-bar__cta" label="Sign In" icon={<ArrowIcon />} onClick={login} />
            ))}
            <PrismButton
              id="sp-menu-button"
              variant={menuOpen ? 'cyan' : 'ghost'}
              morph="swap"
              label={menuOpen ? 'Close' : 'Menu'}
              icon={menuOpen ? <CloseIcon /> : <MenuBarsIcon />}
              ariaLabel={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="sp-menu"
              pressed={menuOpen}
              style={{ width: 104 }}
              onClick={() => setMenuOpen((open) => !open)}
            />
          </div>
        </header>

        <div
          id="sp-menu"
          className={`sp-menu${menuOpen ? ' is-open' : ''}`}
          aria-hidden={!menuOpen}
          inert={menuOpen ? undefined : ''}
        >
          <div className="sp-menu__body" ref={panelRef}>
            {menuOpen && (
              <div className="sp-menu__grid">
                <nav className="sp-menu__main" aria-label="Support">
                  <p className="sp-eyebrow">Support</p>
                  <ol className="sp-menu__list sp-menu__rise">
                    {navItems.map((item, i) => (
                      <li key={item.path} style={{ '--n': i }}>
                        <Link
                          to={item.path}
                          className={`sp-menu-link${isActive(item.path) ? ' is-current' : ''}`}
                          aria-current={isActive(item.path) ? 'page' : undefined}
                          onClick={() => setMenuOpen(false)}
                        >
                          <span className="sp-menu-link__num">{String(i + 1).padStart(2, '0')}</span>
                          <span className="sp-menu-link__text">{item.label}</span>
                          <span className="sp-menu-link__arrow" aria-hidden="true"><ArrowIcon size={22} /></span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                </nav>
                <div className="sp-menu__side sp-menu__rise">
                  <section className="sp-menu__group" aria-label="Your account" style={{ '--n': navItems.length }}>
                    <p className="sp-eyebrow sp-eyebrow--quiet">{isAuthenticated ? 'Signed in as' : 'Your account'}</p>
                    {isAuthenticated ? (
                      <>
                        <p className="sp-menu__account">{user?.email}</p>
                        <button type="button" className="sp-menu__page" onClick={handleLogoutClick}>Sign Out</button>
                      </>
                    ) : (
                      <button type="button" className="sp-menu__page" onClick={() => { setMenuOpen(false); login(); }}>
                        Sign In <ArrowIcon size={16} />
                      </button>
                    )}
                  </section>
                  <section className="sp-menu__group" aria-label="Elebyte" style={{ '--n': navItems.length + 1 }}>
                    <p className="sp-eyebrow sp-eyebrow--quiet">Elebyte</p>
                    {ELEBYTE_LINKS.map((link) => (
                      <a key={link.href} className="sp-menu__page sp-menu__page--ext" href={link.href}>
                        {link.label} <ArrowIcon size={14} />
                      </a>
                    ))}
                  </section>
                </div>
              </div>
            )}
          </div>
          <div className="sp-menu__slats" aria-hidden="true">
            {Array.from({ length: 14 }, (_, i) => <span key={i} className="sp-menu__slat" style={{ '--i': i }} />)}
          </div>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={handleLogoutCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Sign Out</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to sign out? You'll need to sign in again to access your tickets and submit new ones.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleLogoutConfirm} variant="contained" color="primary">
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Header;
